import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { PrimaryCTA, Eyebrow, SectionNum } from "@/components/ui-bits";
import { INDUSTRIES } from "@/data/industries";
import { PRACTICE_AREAS } from "@/data/practiceAreas";
import { ArrowUpRight } from "lucide-react";

const PRACTICE_AREA_MAP = Object.fromEntries(PRACTICE_AREAS.map((p) => [p.slug, p]));

export default function Industries() {
  return (
    <Layout>
      <SEO
        title="Industries"
        description="Shelton Law PLLC advises clients across space, defense, artificial intelligence, and semiconductor industries — sectors where corporate law meets federal regulation."
        path="/industries"
      />

      <section className="border-b border-hairline" data-testid="industries-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16">
          <Eyebrow>Industries</Eyebrow>
          <h1 className="display-1 text-ink mt-6 max-w-4xl">Sectors served.</h1>
          <p className="mt-8 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
            The firm&apos;s practice areas apply across industries. The four sectors below are where the firm has the deepest client experience — where corporate law meets federal regulation, foreign investment review, and export policy.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20" data-testid="industries-list">
        <ul className="border-t border-hairline">
          {INDUSTRIES.map((ind) => {
            const linkTo = ind.legacyServicesSlug ? `/services/${ind.legacyServicesSlug}` : null;
            return (
              <li key={ind.slug} className="border-b border-hairline" data-testid={`industry-${ind.slug}`}>
                <div className="-mx-3 px-3">
                  <div className="grid grid-cols-12 gap-6 lg:gap-10 py-10 lg:py-14 items-baseline">
                    <SectionNum className="col-span-2 sm:col-span-1 self-start pt-3">{ind.number}</SectionNum>
                    <div className="col-span-10 sm:col-span-5">
                      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.06] tracking-tight" style={{ color: "#C82828" }}>
                        {ind.name}
                      </h2>
                      <p className="mt-3 text-ink-soft italic font-serif text-lg leading-snug">{ind.kicker}</p>
                    </div>
                    <div className="col-span-12 sm:col-span-6 text-ink text-[1.0625rem] leading-[1.7]">
                      {ind.lead}
                      {linkTo && (
                        <div className="mt-5">
                          <Link
                            to={linkTo}
                            className="inline-flex items-center gap-2 text-ink border-b border-brass pb-1 hover:text-brass transition-colors text-[11px] tracking-[0.22em] uppercase font-semibold"
                            data-testid={`industry-${ind.slug}-detail-link`}
                          >
                            Detailed practice content
                            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                          </Link>
                        </div>
                      )}
                      {ind.relatedPracticeAreas && ind.relatedPracticeAreas.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-hairline-soft" data-testid={`industry-related-${ind.slug}`}>
                          <p className="text-[10px] uppercase tracking-[0.28em] text-ink-soft font-semibold mb-4">
                            Related Practice Areas
                          </p>
                          <ul className="flex flex-wrap gap-x-5 gap-y-2">
                            {ind.relatedPracticeAreas
                              .map((slug) => PRACTICE_AREA_MAP[slug])
                              .filter(Boolean)
                              .map((pa) => (
                                <li key={pa.slug}>
                                  <Link
                                    to={`/services/${pa.slug}`}
                                    className="inline-flex items-center gap-1.5 text-[13px] text-ink border-b border-brass/50 pb-0.5 hover:text-brass hover:border-brass transition-colors"
                                    data-testid={`industry-${ind.slug}-related-${pa.slug}`}
                                  >
                                    {pa.name}
                                    <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                                  </Link>
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="bg-ink text-ivory" data-testid="industries-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-5">Sector Inquiries</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
            Matters at the intersection of corporate law and federal regulation.
          </h2>
          <div className="mt-10 flex justify-center">
            <PrimaryCTA to="/contact" testId="industries-cta-button">Contact the Firm</PrimaryCTA>
          </div>
        </div>
      </section>
    </Layout>
  );
}
